import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { DGT_Pagination } from "@/utils/utils";
import { SCROLL_THRESHOLD_PX } from "./mask-multi-select-constant";
import { MData } from "./types";
import { useMaskMultiSelect } from "./use-mask-multi-select";

export interface UseMaskMultiSelectScrollArgs<T extends MData> {
  options?: T[];
  value: T[];
  onChange?: (v?: T[], details?: any) => void;
  onSearch?: (v: string) => void;
  handleBlur?: any;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  pagination: DGT_Pagination;
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  totalElements?: number;
  loading?: boolean;
}

export function useMaskMultiSelectScroll<T extends MData>({
  options,
  value,
  onChange,
  onSearch,
  handleBlur,
  name,
  disabled,
  readOnly,
  pagination,
  setPagination,
  totalElements,
  loading,
}: UseMaskMultiSelectScrollArgs<T>) {
  const dedupeKey = useMemo(
    () => (options && options.length > 0 ? Object.keys(options[0])[0] : "uuid"),
    [options]
  );

  const { items, atEnd, onScroll } = useInfiniteScroll<T>({
    pagination,
    setPagination,
    content: options,
    totalElements,
    isFetching: loading,
    dedupeKey,
    thresholdPx: SCROLL_THRESHOLD_PX,
  });

  const base = useMaskMultiSelect<T>({
    options: items,
    value,
    onChange,
    onSearch,
    handleBlur,
    name,
    disabled,
    readOnly,
  });

  // Server-side search owns the result set, so a new term restarts pagination.
  const handleSearchChange = useCallback(
    (v: string) => {
      base.handleSearchChange(v);
      if (onSearch) setPagination((prev) => ({ ...prev, page: 0 }));
    },
    [base, onSearch, setPagination]
  );

  return {
    ...base,
    handleSearchChange,
    onScroll,
    loadingMore: !!loading && !atEnd,
  };
}
